import React from "react";
import { Phone, Mail, Package, MapPin, CheckSquare, Square, Clock, Sparkles, Gift, Pencil, MessageCircle, Loader2 } from "lucide-react";
import { formatDate, formatSetupTime, formatDateTime } from "../../utils/datetime";
import MapNavButton from "../MapNavButton";
import { computePayment, PAYMENT_STATUS_META, bookingSourceMeta, rupee, getAddOns } from "./bookingPayment";

const STATUS_COLORS = {
  confirmed: "bg-green-100 text-green-700",
  pending: "bg-yellow-100 text-yellow-700",
  cancelled: "bg-red-100 text-red-700",
  completed: "bg-blue-100 text-blue-700",
  rejected: "bg-red-100 text-red-700",
  refunded: "bg-gray-100 text-gray-700",
};

// Terminal booking states win over the payment gate — flipping a cancelled
// or completed booking back to "pending" would misrepresent the lifecycle.
const TERMINAL_STATUSES = new Set(["cancelled", "rejected", "completed", "refunded"]);

/**
 * The displayed booking status must agree with payment reality: if the
 * customer hasn't paid, the booking is NOT confirmed — regardless of what the
 * DB has stored (legacy data from before the iter181 gating fix can show
 * "confirmed" + "pending" together).
 */
export const deriveStatus = (b) => {
  const stored = (b.booking_status || b.status || "").toLowerCase();
  if (TERMINAL_STATUSES.has(stored)) return stored;
  const paid = (b.payment_status || "").toLowerCase() === "paid";
  if (!paid) return "pending";
  return stored || "confirmed";
};

export const BookingRow = ({ b, index, isSelected, onToggle, onEdit, onSendBalanceLink, isSendingBalance }) => {
  const status = deriveStatus(b);
  const pay = computePayment(b);
  const payMeta = PAYMENT_STATUS_META[pay.status];
  const src = bookingSourceMeta(b);
  const addOns = getAddOns(b);

  return (
    <tr
      className={`border-b border-gray-50 hover:bg-[#FFFAF5] transition-colors ${isSelected ? "bg-[#E83E8C]/5" : ""}`}
      data-testid={`booking-row-${index}`}
    >
      <td className="p-3">
        <button onClick={onToggle} data-testid={`select-row-${index}`}>
          {isSelected ? <CheckSquare size={16} className="text-[#E83E8C]" /> : <Square size={16} className="text-gray-300" />}
        </button>
      </td>
      {/* Full Booking ID + source badge */}
      <td className="p-3">
        <span className="font-mono text-xs text-[#1E1B4B] whitespace-nowrap" data-testid={`booking-id-${index}`}>{b.id || "—"}</span>
        <span className={`mt-1 px-2 py-0.5 rounded-full text-[9px] font-bold uppercase tracking-wide flex items-center gap-1 w-fit ${src.cls}`} data-testid={`booking-source-${index}`}>
          {src.label === "WhatsApp" ? <Phone size={8} /> : src.label === "DoozyGo Magic" ? <Sparkles size={8} /> : null}
          {src.label}
        </span>
      </td>
      <td className="p-3 font-medium text-[#1E1B4B] whitespace-nowrap">{formatDate(b.date)}</td>
      <td className="p-3 text-xs text-[#1E1B4B] whitespace-nowrap min-w-[160px]">
        {(() => {
          const slot = b.setup_time || b.event_time || b.time_slot || "";
          const pretty = formatSetupTime(slot);
          return pretty ? (
            <span className="inline-flex items-center gap-1" title={slot} data-testid={`booking-setup-time-${index}`}>
              <Clock size={10} className="text-[#E83E8C] shrink-0" /> {pretty}
            </span>
          ) : (
            <span className="text-[#94a3b8]" data-testid={`booking-setup-time-${index}`}>—</span>
          );
        })()}
      </td>
      <td className="p-3">
        <p className="font-medium text-[#1E1B4B] truncate max-w-[140px]">{b.customer_name || "—"}</p>
      </td>
      <td className="p-3">
        <div className="flex flex-col gap-0.5">
          <span className="text-xs text-[#475569] flex items-center gap-1"><Phone size={10} /> {b.customer_phone || "—"}</span>
          {b.customer_email && <span className="text-xs text-[#94a3b8] flex items-center gap-1 truncate max-w-[150px]"><Mail size={10} /> {b.customer_email}</span>}
        </div>
      </td>
      <td className="p-3">
        <span className="px-2 py-0.5 bg-[#E83E8C]/10 text-[#E83E8C] rounded-full text-xs font-medium flex items-center gap-1 w-fit">
          <Package size={10} /> {b.package || "—"}
        </span>
        {b.design_image_url && (b.booking_source || "").toUpperCase() === "DOOZYGO_MAGIC" && (
          <a href={b.design_image_url} target="_blank" rel="noopener noreferrer"
            className="mt-1 px-2 py-0.5 bg-[#8b5cf6]/10 text-[#8b5cf6] rounded-full text-[10px] font-semibold flex items-center gap-1 w-fit hover:bg-[#8b5cf6]/20 transition-colors"
            title="View the customer's AI design reference" data-testid={`booking-magic-design-${index}`}>
            <Sparkles size={9} /> Magic design
          </a>
        )}
        {/* Add-ons selected by the customer */}
        {addOns.length > 0 && (
          <span className="mt-1 px-2 py-0.5 bg-[#FF9F1C]/10 text-[#B26B00] rounded-full text-[10px] font-semibold flex items-center gap-1 w-fit"
            title={addOns.join(", ")} data-testid={`booking-addons-${index}`}>
            <Gift size={9} /> {addOns.length} add-on{addOns.length > 1 ? "s" : ""}
          </span>
        )}
      </td>
      <td className="p-3 text-xs text-[#475569] max-w-[100px] truncate">{b.occasion || "—"}</td>
      <td className="p-3 text-xs text-[#475569] max-w-[180px]">
        <div className="flex items-center gap-1.5">
          <span className="flex items-start gap-1 truncate flex-1" title={b.address}><MapPin size={10} className="shrink-0 mt-0.5" /> {b.address || "—"}</span>
          <MapNavButton lat={b.location_lat} lng={b.location_lng} address={b.address} compact testId={`booking-navigate-${index}`} />
        </div>
      </td>
      {/* Amount breakdown: Total / Paid / Balance as 3 columns */}
      <td className="p-3 whitespace-nowrap font-semibold text-[#1E1B4B]" data-testid={`amount-total-${index}`}>{rupee(pay.total)}</td>
      <td className="p-3 whitespace-nowrap font-semibold text-green-600" data-testid={`amount-paid-${index}`}>{rupee(pay.paid)}</td>
      <td className={`p-3 whitespace-nowrap font-semibold ${pay.balance > 0 ? "text-orange-600" : "text-[#94a3b8]"}`} data-testid={`amount-balance-${index}`}>{rupee(pay.balance)}</td>
      <td className="p-3">
        <span className={`px-2 py-0.5 rounded-full text-[10px] font-medium whitespace-nowrap ${payMeta.cls}`} data-testid={`payment-status-${index}`}>
          {payMeta.label}
        </span>
        {pay.status === "incomplete" && onSendBalanceLink && (
          <button onClick={() => onSendBalanceLink(b)} disabled={isSendingBalance}
            data-testid={`send-balance-${index}`}
            title={`Send a WhatsApp link to collect the ₹${pay.balance.toLocaleString("en-IN")} balance`}
            className="mt-1 px-2 py-1 rounded-lg bg-[#25D366]/10 text-[#128C7E] text-[10px] font-bold flex items-center gap-1 w-fit hover:bg-[#25D366]/20 transition-colors disabled:opacity-50">
            {isSendingBalance ? <Loader2 size={10} className="animate-spin" /> : <MessageCircle size={10} />}
            {isSendingBalance ? "Sending…" : "Send balance link"}
          </button>
        )}
      </td>
      <td className="p-3">
        <span className={`px-2 py-0.5 rounded-full text-[10px] font-medium ${STATUS_COLORS[status] || "bg-gray-100 text-gray-700"}`}>
          {status}
        </span>
      </td>
      <td className="p-3 text-xs text-[#475569] truncate max-w-[100px]">{b.sales_executive || "—"}</td>
      <td className="p-3 text-xs text-[#94a3b8] whitespace-nowrap">{formatDateTime(b.created_at)}</td>
      <td className="p-3">
        {onEdit && (
          <button onClick={() => onEdit(b)} data-testid={`booking-edit-${index}`}
            className="p-1.5 rounded-lg text-[#475569] hover:bg-[#E83E8C]/10 hover:text-[#E83E8C] transition-colors" title="Edit customer & booking details">
            <Pencil size={14} />
          </button>
        )}
      </td>
    </tr>
  );
};
